import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { FieldArray, Form } from 'formik';
import { ArrowLeftRight } from 'lucide-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  CloseIcon,
  DownArrowIcon,
  UpArrowIcon,
} from '../../../assets/icons/BlockActionIcons';
import { POST_BUTTON_LABELS } from '../../../constants/post.constants';
import { TE_CONSTANTS } from '../../../constants/textEditor.constants';
import {
  moveDownTarget,
  moveUpTarget,
  toBlockArray,
} from '../../../helpers/formikBlockValues';
import { useBlockRemoval } from '../../../hooks/useBlockRemoval';
import { createNewBlock } from '../../../initial-states/textBlocks.initialState';
import { DraftInterfaceState } from '../../../interfaces/post.interface';
import type {
  BlockType,
  TextBlock,
} from '../../../interfaces/textEditor.interface';
import { draftSlice } from '../../../store/draft.store';
import { RenderBlock } from '../../TextEditor/BlockTypesRender';
import ButtonComponent from '../../UI/Button/Button.component';
import { DraftFormRenderProps } from './DraftFormRender';
import '../../TextEditor/text-editor.scss';

export const DraftFormRender = (
  props: DraftFormRenderProps,
): React.ReactElement => {
  const {
    handleSubmit,
    values,
    handleMenuClick,
    menuAnchors,
    handleMenuClose,
    handleBlockTypeChange,
    blockTypes,
    setFieldValue,
    isSubmitting,
    handleSave,
    payload,
    handleOpenHeadingMenu,
    headingMenuAnchors,
    handleCloseHeadingMenu,
    handleHeadingLevelChange,
    fileInputRef,
    touched,
    errors,
    isEditing,
    isEditingUserPost,
    hideSubmitButton,
  } = props;
  const params = useParams();
  const dispatch = useDispatch();
  const { username, draftId } = params;
  const [arrayOfValues, setArrayOfValues] = useState([]);
  const {
    isEditingDraft,
    isAddingDraft,
    isAddingPostReplyAsDraft,
    savedImageKey,
  } = useSelector((state: DraftInterfaceState) => state.draftStore);
  const { isEditingPostReplyDraft } = useSelector(
    (state: any) => state.postReplyDraftStore ?? {},
  );
  const { RESET_IMAGE_KEYS } = draftSlice.actions;

  const handleKeyRemoved = React.useCallback(
    (key: string) => {
      const storedImageKeys = (savedImageKey as string[]) ?? [];
      dispatch(
        RESET_IMAGE_KEYS(
          storedImageKeys.filter((imageKey: string) => imageKey !== key),
        ),
      );
    },
    [savedImageKey, dispatch, RESET_IMAGE_KEYS],
  );

  // TRIL-615: the removal handler and its error banner live in useBlockRemoval, shared with the
  // other composer. There is deliberately no wiring left here to get wrong — review found twice
  // that a component passing `onError` could silently stop passing it with the suite still green.
  const { removeBlock, imageDeleteError, isRemoving } = useBlockRemoval({
    username: username as string,
    draftId: draftId as string,
    onKeyRemoved: handleKeyRemoved,
  });

  useEffect(() => {
    if (isEditing) {
      setArrayOfValues(values as any);
    } else {
      setArrayOfValues(values as any);
    }
  }, [values]);

  const renderBlockRow = (
    block: TextBlock,
    index: number,
    arrayHelpers: any,
    useEditingContext = false,
  ) => (
    <div
      key={block.id}
      className={`Text__Editor__Container${useEditingContext ? ' DRAFT' : ''}`}
    >
      <div className="GridItem">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="block-type-trigger"
              aria-label="Change block type"
            >
              <ArrowLeftRight size={16} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="block-type-menu"
              sideOffset={4}
              align="start"
            >
              {blockTypes.map((type) => (
                <DropdownMenu.Item
                  key={type}
                  className="block-type-menu__item"
                  onSelect={() => {
                    handleBlockTypeChange(
                      index,
                      type as BlockType,
                      setFieldValue,
                      values,
                    );
                  }}
                >
                  {type}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      <div
        className={`GridItem${useEditingContext ? ' Input__Area flex-grow' : ''}`}
      >
        <RenderBlock
          arrayHelpers={arrayHelpers}
          block={block}
          fileInputRef={fileInputRef}
          handleCloseHeadingMenu={handleCloseHeadingMenu}
          handleHeadingLevelChange={handleHeadingLevelChange}
          handleOpenHeadingMenu={handleOpenHeadingMenu}
          headingMenuAnchors={headingMenuAnchors}
          index={index}
          setFieldValue={setFieldValue}
          values={values}
        />
        {
          // @ts-ignore
          touched.blocks &&
            // @ts-ignore
            touched.blocks[index] &&
            errors.blocks &&
            // @ts-ignore
            errors.blocks[index] && (
              <div className="error-message text-red-500 text-sm mt-1">
                {
                  // @ts-ignore
                  errors.blocks[index].data
                }
              </div>
            )
        }
      </div>
      <div className="GridItem">
        <button
          type="button"
          data-testid="move-content-block-up"
          className="block-action-icon block-action-icon--saphir"
          disabled={toBlockArray(values).length === 1}
          onClick={() => {
            arrayHelpers.move(index, moveUpTarget(index));
          }}
          aria-label="Move block up"
        >
          <UpArrowIcon size={16} />
        </button>
        <button
          type="button"
          data-testid="move-content-block-down"
          className="block-action-icon block-action-icon--saphir"
          disabled={toBlockArray(values).length === 1}
          onClick={() => {
            arrayHelpers.move(index, moveDownTarget(values, index));
          }}
          aria-label="Move block down"
        >
          <DownArrowIcon size={16} />
        </button>
        <button
          type="button"
          data-testid="delete-content-block"
          className="block-action-icon block-action-icon--danger"
          disabled={toBlockArray(values).length === 1}
          onClick={() => removeBlock(block, arrayHelpers, index)}
          aria-label="Remove block"
        >
          <CloseIcon size={18} />
        </button>
      </div>
    </div>
  );

  const renderDraftBlockRow = (
    block: TextBlock,
    index: number,
    arrayHelpers: any,
  ) => (
    <div key={block.id} className="Text__Editor__Container">
      <div className="GridItem">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="block-type-trigger"
              aria-label="Change block type"
            >
              <ArrowLeftRight size={16} />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="block-type-menu"
              sideOffset={4}
              align="start"
            >
              {blockTypes.map((type) => (
                <DropdownMenu.Item
                  key={type}
                  className="block-type-menu__item"
                  onSelect={() => {
                    handleBlockTypeChange(
                      index,
                      type as BlockType,
                      setFieldValue,
                      values,
                    );
                  }}
                >
                  {type}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
      <div className="GridItem">
        <RenderBlock
          arrayHelpers={arrayHelpers}
          block={block}
          fileInputRef={fileInputRef}
          handleCloseHeadingMenu={handleCloseHeadingMenu}
          handleHeadingLevelChange={handleHeadingLevelChange}
          handleOpenHeadingMenu={handleOpenHeadingMenu}
          headingMenuAnchors={headingMenuAnchors}
          index={index}
          setFieldValue={setFieldValue}
          values={values}
        />
        {
          // @ts-ignore
          touched.blocks &&
            // @ts-ignore
            touched.blocks[index] &&
            errors.blocks &&
            // @ts-ignore
            errors.blocks[index] && (
              <div className="error-message text-red-500 text-sm mt-1">
                {
                  // @ts-ignore
                  errors.blocks[index].data
                }
              </div>
            )
        }
      </div>
      <div className="GridItem">
        <button
          type="button"
          className="block-action-icon block-action-icon--saphir"
          disabled={toBlockArray(values).length === 1 || isRemoving}
          onClick={() => {
            arrayHelpers.move(index, moveUpTarget(index));
          }}
          aria-label="Move block up"
        >
          <UpArrowIcon size={16} />
        </button>
        <button
          type="button"
          className="block-action-icon block-action-icon--saphir"
          disabled={toBlockArray(values).length === 1 || isRemoving}
          onClick={() => {
            arrayHelpers.move(index, moveDownTarget(values, index));
          }}
          aria-label="Move block down"
        >
          <DownArrowIcon size={16} />
        </button>
        <button
          type="button"
          className="block-action-icon block-action-icon--danger"
          data-testid="delete-content-block"
          disabled={toBlockArray(values).length === 1}
          onClick={() => removeBlock(block, arrayHelpers, index)}
          aria-label="Remove block"
        >
          <CloseIcon size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <Form className="Text__Editor" onSubmit={handleSubmit}>
      {imageDeleteError && (
        <div
          className="error-message text-red-500 text-sm mt-1"
          role="alert"
          data-testid="image-delete-error"
        >
          {imageDeleteError}
        </div>
      )}
      <FieldArray
        name="blocks"
        render={(arrayHelpers) => (
          <>
            {isEditingPostReplyDraft
              ? // @ts-ignore
                values?.blocks.map((block: TextBlock, index: number) =>
                  renderBlockRow(block, index, arrayHelpers),
                )
              : isAddingDraft || isAddingPostReplyAsDraft
                ? // @ts-ignore
                  values?.map((block: TextBlock, index: number) =>
                    renderDraftBlockRow(block, index, arrayHelpers),
                  )
                : isEditingDraft
                  ? // @ts-ignore
                    values?.blocks.map((block: TextBlock, index: number) =>
                      renderBlockRow(block, index, arrayHelpers, true),
                    )
                  : // Fallback when no Redux flag is set (e.g. EditRewrite page, or
                    // a draft / reply-draft detail view after an edit). `values` may
                    // be the bare blocks array OR a { blocks } object — handle both,
                    // otherwise the object shape throws "values.map is not a function".
                    (Array.isArray(values)
                      ? values
                      : ((values as any)?.blocks ?? [])
                    ).map((block: TextBlock, index: number) =>
                      renderBlockRow(block, index, arrayHelpers),
                    )}
            <div className="Grid">
              <div className="GridItem">
                <ButtonComponent
                  className={'Text__Editor__Add'}
                  variant={'saphir'}
                  onClick={() => {
                    arrayHelpers.push(createNewBlock('Paragraph'));
                  }}
                >
                  {TE_CONSTANTS.ADD_CONTENT_BLOCK}
                </ButtonComponent>
              </div>
              {!hideSubmitButton && (
                <div className="GridItem">
                  <button
                    type="submit"
                    className="btn btn--primary ADD__DRAFT__BUTTON"
                    disabled={isSubmitting}
                    onClick={() => {
                      handleSave(payload);
                    }}
                  >
                    {POST_BUTTON_LABELS.DONE_EDITING}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      />
    </Form>
  );
};
